import React, { useCallback, useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  ClipboardList,
  LayoutDashboard,
  MessageSquare,
  MoreHorizontal,
  Package,
  ScanLine,
  Truck,
  Users,
  Wallet,
  Warehouse,
} from 'lucide-react';
import {
  BottomNavItem,
  buildBottomNavItems,
  resolveHomePath,
} from '../config/navigation';
import { getStoredRepresentativeId, getStoredRoleName, getUserPermissions, hasPermission } from '../api/auth';
import { AUTH_USER_CHANGED_EVENT } from '../constants/auth-events';

const ICONS: Record<BottomNavItem['icon'], React.ReactNode> = {
  orders: <ClipboardList size={20} strokeWidth={2} />,
  rounds: <Truck size={20} strokeWidth={2} />,
  warehouse: <Warehouse size={20} strokeWidth={2} />,
  finance: <Wallet size={20} strokeWidth={2} />,
  messages: <MessageSquare size={20} strokeWidth={2} />,
  today: <LayoutDashboard size={20} strokeWidth={2} />,
  pickups: <Package size={20} strokeWidth={2} />,
  stock: <Package size={20} strokeWidth={2} />,
  scan: <ScanLine size={20} strokeWidth={2} />,
  drivers: <Users size={20} strokeWidth={2} />,
  more: <MoreHorizontal size={20} strokeWidth={2} />,
};

function pathMatches(to: string, pathname: string): boolean {
  if (pathname === to) return true;
  if (to === '/all-orders') {
    return pathname.startsWith('/unified-orders') || pathname.startsWith('/orders/');
  }
  if (to === '/all-rounds') {
    return pathname.startsWith('/rounds');
  }
  if (to === '/rep/rp-rounds') {
    return (
      pathname === '/rep/rp-rounds'
      || pathname.startsWith('/rep/rp-rounds/')
    );
  }
  if (to === '/rep/linehaul') {
    return pathname === '/rep/linehaul' || pathname.startsWith('/rep/linehaul/');
  }
  if (to === '/warehouse/rounds') {
    return pathname === '/warehouse/rounds' || pathname.startsWith('/warehouse/loading');
  }
  if (to === '/warehouse/portal') {
    return pathname === '/warehouse/portal' || pathname.startsWith('/warehouse/loading');
  }
  if (to === '/warehouse/receive') {
    return pathname === '/warehouse/receive' || pathname.startsWith('/warehouse/receive/');
  }
  return pathname.startsWith(`${to}/`);
}

interface MobileBottomNavProps {
  onMore: () => void;
}

export function MobileBottomNav({ onMore }: MobileBottomNavProps) {
  const location = useLocation();
  const [items, setItems] = useState<BottomNavItem[]>(() =>
    buildBottomNavItems(getUserPermissions(), code => hasPermission(code), {
      representativeId: getStoredRepresentativeId(),
      roleName: getStoredRoleName(),
    }),
  );

  const refresh = useCallback(() => {
    setItems(
      buildBottomNavItems(getUserPermissions(), code => hasPermission(code), {
        representativeId: getStoredRepresentativeId(),
      roleName: getStoredRoleName(),
      }),
    );
  }, []);

  useEffect(() => {
    refresh();
    window.addEventListener(AUTH_USER_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(AUTH_USER_CHANGED_EVENT, refresh);
  }, [refresh]);

  const homeTo = resolveHomePath(getUserPermissions(), undefined, getStoredRoleName());

  return (
    <nav className="bottom-nav" data-testid="bottom-nav" aria-label="Основная навигация">
      {items.map(item => {
        if (item.more) {
          return (
            <button
              key={item.id}
              type="button"
              className="bottom-nav__item"
              onClick={onMore}
              data-testid="bottom-nav-more"
            >
              <span className="bottom-nav__icon">{ICONS.more}</span>
              <span className="bottom-nav__label">{item.label}</span>
            </button>
          );
        }

        const to = item.to || homeTo;
        const active = pathMatches(to, location.pathname);
        return (
          <NavLink
            key={item.id}
            to={to}
            end={to === '/all-orders' || to === '/all-rounds'}
            className={`bottom-nav__item${active ? ' bottom-nav__item--active' : ''}`}
            data-testid={`bottom-nav-${item.id}`}
          >
            <span className="bottom-nav__icon">{ICONS[item.icon]}</span>
            <span className="bottom-nav__label">{item.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}
